import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import StoricoCliente from "@/components/StoricoCliente";
import { 
  Plus, 
  Search, 
  Users,
  Phone,
  Mail,
  Trash2,
  Edit,
  User,
  Calendar,
  Cake,
  CreditCard,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  History,
  Euro,
  X,
  Download,
  Check,
  Loader2,
  Upload,
  FileSpreadsheet,
  ChevronDown,
  Shield,
  FileSignature,
  ClipboardCheck,
  Repeat,
  Sparkles,
  ArrowLeft,
  CalendarDays,
  Settings,
  AlertCircle
} from "lucide-react";
import GDPRConsent from "@/components/GDPRConsent";
import CardCliente from "@/components/clienti/CardCliente";
import ProfiloCapelliForm, { ProfiloCapelliView } from "@/components/ProfiloCapelliForm";
import { format, differenceInYears } from "date-fns";
import { it } from "date-fns/locale";

// Paginazione
const CLIENTI_PER_PAGINA = 50;

// Debounce hook per ricerca fluida
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function Clienti() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("tutti");
  const [paginaCorrente, setPaginaCorrente] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [clienteEsistente, setClienteEsistente] = useState(null);
  
  // Stato per ritorno all'Agenda
  const [returnToAgenda, setReturnToAgenda] = useState(null);
  
  // Debounce per ricerca fluida (150ms)
  const debouncedSearch = useDebounce(searchTerm, 150);
  
  // Google Contacts Import
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [googleContacts, setGoogleContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [fetchingContacts, setFetchingContacts] = useState(false);
  const [needsGoogleConnect, setNeedsGoogleConnect] = useState(false);
  
  // CSV Import
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvContacts, setCsvContacts] = useState([]);
  const [selectedCsvContacts, setSelectedCsvContacts] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  
  // Card Virtuale states
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [selectedClienteCard, setSelectedClienteCard] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [ricaricaAmount, setRicaricaAmount] = useState("");
  const [ricaricaNote, setRicaricaNote] = useState("");
  const [cardsStatus, setCardsStatus] = useState({});

  // Helper: ritorna l'elenco dei campi obbligatori/consigliati mancanti per un cliente.
  // Usato per mostrare il pallino lampeggiante sull'ingranaggio Anagrafica.
  // NB: solo telefono e data di nascita (email/genere non sono obbligatori per il lampeggio).
  const getCampiMancanti = useCallback((cliente) => {
    if (!cliente) return [];
    const mancanti = [];
    if (!cliente.telefono || !String(cliente.telefono).trim()) mancanti.push('telefono');
    if (!cliente.data_nascita || !String(cliente.data_nascita).trim()) mancanti.push('data nascita');
    return mancanti;
  }, []);

  // Conteggio totale clienti con anagrafica incompleta (per banner alert)
  const clientiAnagraficaIncompleta = useMemo(() => {
    if (!clienti || !Array.isArray(clienti)) return [];
    return clienti.filter(c => getCampiMancanti(c).length > 0);
  }, [clienti, getCampiMancanti]);

  
  // Consulenze status (per mostrare badge "Consulenza fatta")
  const [consulenzeStatus, setConsulenzeStatus] = useState({});
  
  // GDPR states
  const [gdprDialogOpen, setGdprDialogOpen] = useState(false);
  const [selectedClienteGDPR, setSelectedClienteGDPR] = useState(null);
  
  // Storico Cliente
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [selectedClienteStorico, setSelectedClienteStorico] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    telefono: "",
    email: "",
    data_nascita: "",
    genere: "",
    note: "",
    profilo_capelli: {}
  });

  // Calcola età da data di nascita
  const calcolaEta = (dataNascita) => {
    if (!dataNascita) return null;
    try {
      return differenceInYears(new Date(), new Date(dataNascita));
    } catch {
      return null;
    }
  };

  // Initial data fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchClienti();
    fetchCardsStatus();
    fetchConsulenzeStatus();
  }, []);

  // Gestisci parametro URL ?id= o ?clienteId= per aprire scheda cliente
  useEffect(() => {
    const clienteId = searchParams.get('id') || searchParams.get('clienteId');
    
    // Salva stato per ritorno all'Agenda se presente (router state OPPURE sessionStorage)
    if (location.state?.fromAgenda) {
      const ret = {
        data: location.state.data,
        appuntamentoId: location.state.appuntamentoId
      };
      setReturnToAgenda(ret);
      // Persisti in sessionStorage per sopravvivere a refresh / navigate replace
      try { sessionStorage.setItem('returnToAgenda', JSON.stringify(ret)); } catch (e) { console.warn('sessionStorage non disponibile:', e); }
    } else {
      // Recupera da sessionStorage se ancora in vita (sessione corrente)
      try {
        const saved = sessionStorage.getItem('returnToAgenda');
        if (saved) {
          const ret = JSON.parse(saved);
          if (ret?.data) setReturnToAgenda(ret);
        }
      } catch (e) { console.warn('sessionStorage non disponibile:', e); }
    }
    
    if (clienteId && clienti.length > 0) {
      const cliente = clienti.find(c => c.id === clienteId);
      if (cliente) {
        const editMode = searchParams.get('edit') === 'true';
        if (editMode) {
          openEditDialog(cliente);
        } else {
          setSelectedClienteStorico(cliente);
          setStoricoOpen(true);
        }
        // Rimuovi il parametro dall'URL ma mantieni lo state
        navigate('/clienti', { replace: true, state: location.state });
      }
    }
  }, [searchParams, clienti, navigate, location.state]);

  // Funzione per tornare all'Agenda
  const handleBackToAgenda = () => {
    // Recupera pendingAppuntamento per ripristinare il dialog se presente
    let hasPending = false;
    try {
      hasPending = !!sessionStorage.getItem('pendingAppuntamento');
    } catch (e) { console.warn('sessionStorage non disponibile:', e); }
    // Pulisci sessionStorage al ritorno
    try { sessionStorage.removeItem('returnToAgenda'); } catch (e) { console.warn('sessionStorage non disponibile:', e); }
    if (returnToAgenda) {
      const params = new URLSearchParams();
      params.set('data', returnToAgenda.data);
      if (returnToAgenda.appuntamentoId) params.set('highlight', returnToAgenda.appuntamentoId);
      if (hasPending) params.set('restoreForm', '1');
      navigate(`/agenda?${params.toString()}`, { state: { restoreFromCliente: hasPending } });
    } else {
      navigate('/agenda', { state: { restoreFromCliente: hasPending } });
    }
  };

  const fetchConsulenzeStatus = async () => {
    try {
      const response = await axios.get(`${API}/consulenze/status-by-cliente`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsulenzeStatus(response.data);
    } catch (error) {
      console.error('Errore caricamento consulenze status:', error);
    }
  };

  const fetchCardsStatus = async () => {
    try {
      const response = await axios.get(`${API}/cards/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCardsStatus(response.data);
    } catch (error) {
      console.error('Errore caricamento cards status:', error);
    }
  };

  const fetchClienti = async () => {
    try {
      // Carica tutti i clienti (fino a 5000) per permettere ricerca locale completa
      const response = await axios.get(`${API}/clienti?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClienti(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  // Verifica se cliente esiste già (per nome+cognome o telefono)
  const verificaClienteEsistente = useCallback((nome, cognome, telefono) => {
    if (!nome && !telefono) {
      setClienteEsistente(null);
      return;
    }
    
    const telefonoPulito = (telefono || '').replace(/\s/g, '');
    const nomeLower = (nome || '').toLowerCase().trim();
    const cognomeLower = (cognome || '').toLowerCase().trim();
    
    const esistente = clienti.find(c => {
      // Controlla per telefono (se presente)
      if (telefonoPulito && telefonoPulito.length >= 6) {
        const telCliente = (c.telefono || '').replace(/\s/g, '');
        if (telCliente && telCliente.includes(telefonoPulito)) {
          return true;
        }
      }
      
      // Controlla per nome + cognome
      if (nomeLower && cognomeLower) {
        const nomeCliente = (c.nome || '').toLowerCase().trim();
        const cognomeCliente = (c.cognome || '').toLowerCase().trim();
        if (nomeCliente === nomeLower && cognomeCliente === cognomeLower) {
          return true;
        }
      }
      
      return false;
    });
    
    setClienteEsistente(esistente || null);
  }, [clienti]);

  // Verifica quando cambiano i dati del form
  useEffect(() => {
    if (dialogOpen && !editingCliente) {
      verificaClienteEsistente(formData.nome, formData.cognome, formData.telefono);
    }
  }, [formData.nome, formData.cognome, formData.telefono, dialogOpen, editingCliente, verificaClienteEsistente]);

  // ==================== GOOGLE CONTACTS IMPORT ====================
  
  const openImportDialog = async () => {
    setImportDialogOpen(true);
    setFetchingContacts(true);
    setGoogleContacts([]);
    setSelectedContacts([]);
    
    try {
      const response = await axios.get(`${API}/google/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setGoogleContacts(response.data.contacts);
        // Seleziona tutti i contatti con telefono o email
        const validContacts = response.data.contacts.filter(c => c.telefono || c.email);
        setSelectedContacts(validContacts.map(c => c.google_id));
      }
    } catch (error) {
      if (error.response?.status === 400) {
        // Google non connesso - avvia direttamente il flusso OAuth
        setFetchingContacts(false);
        setNeedsGoogleConnect(true);
      } else {
        toast.error(error.response?.data?.detail || "Errore nel recupero contatti");
        setImportDialogOpen(false);
      }
    } finally {
      setFetchingContacts(false);
    }
  };

  const connectGoogle = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.authorization_url) {
        // Salva lo stato per tornare qui dopo OAuth
        localStorage.setItem('returnToClienti', 'true');
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error("Errore nella connessione a Google");
    }
  };

  // Controlla se tornare dopo OAuth
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_connected') === 'true' || localStorage.getItem('returnToClienti') === 'true') {
      localStorage.removeItem('returnToClienti');
      // Pulisci URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Apri automaticamente il dialog di importazione
      setTimeout(() => {
        openImportDialog();
      }, 500);
    }
  }, []);

  const toggleContactSelection = (googleId) => {
    setSelectedContacts(prev => {
      if (prev.includes(googleId)) {
        return prev.filter(id => id !== googleId);
      } else {
        return [...prev, googleId];
      }
    });
  };

  const selectAllContacts = () => {
    const allIds = googleContacts.map(c => c.google_id);
    setSelectedContacts(allIds);
  };

  const deselectAllContacts = () => {
    setSelectedContacts([]);
  };

  const importSelectedContacts = async () => {
    if (selectedContacts.length === 0) {
      toast.error("Seleziona almeno un contatto");
      return;
    }
    
    setImportLoading(true);
    
    try {
      const contactsToImport = googleContacts.filter(c => 
        selectedContacts.includes(c.google_id)
      );
      
      const response = await axios.post(`${API}/google/contacts/import`, contactsToImport, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setImportDialogOpen(false);
        // Delay per evitare conflitti DOM
        setTimeout(() => {
          fetchClienti();
        }, 150);
      }
    } catch (error) {
      toast.error("Errore nell'importazione");
    } finally {
      setImportLoading(false);
    }
  };

  // CSV Import Functions
  const handleCsvFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      parseCsvContacts(text);
    };
    reader.readAsText(file);
  };

  const parseCsvContacts = (csvText) => {
    const lines = csvText.split('\n');
    if (lines.length < 2) {
      toast.error("File CSV vuoto o non valido");
      return;
    }
    
    // Prima riga = header - rimuovi BOM se presente
    const headerLine = lines[0].replace(/^\uFEFF/, '');
    const headers = headerLine.split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    
    // Trova indici colonne - match esatto
    const findIndex = (names) => {
      for (let i = 0; i < headers.length; i++) {
        const h = headers[i];
        if (names.some(n => h === n)) {
          return i;
        }
      }
      return -1;
    };
    
    // Formato Google Contacts: "First Name", "Last Name", "E-mail 1 - Value", "Phone 1 - Value"
    const firstNameIdx = findIndex(['first name']);
    const lastNameIdx = findIndex(['last name']);
    const emailIdx = findIndex(['e-mail 1 - value']);
    const phoneIdx = findIndex(['phone 1 - value']);
    
    const contacts = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Parse CSV con supporto per virgolette e virgole dentro i campi
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim()); // ultima colonna
      
      const cleanValue = (idx) => {
        if (idx < 0 || idx >= values.length) return '';
        return (values[idx] || '').replace(/"/g, '').trim();
      };
      
      // Estrai First Name e Last Name
      let firstName = cleanValue(firstNameIdx);
      let lastName = cleanValue(lastNameIdx);
      
      // Rimuovi emoji dal nome
      firstName = firstName.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();
      lastName = lastName.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();
      
      // Se il First Name contiene più parole e Last Name è vuoto, splitta
      if (firstName && !lastName) {
        const parts = firstName.split(' ').filter(p => p.length > 0);
        if (parts.length >= 2) {
          firstName = parts[0]; // Prima parola = nome
          lastName = parts.slice(1).join(' '); // Resto = cognome
        }
      }
      
      const email = cleanValue(emailIdx);
      let telefono = cleanValue(phoneIdx);
      // Pulisci telefono - mantieni solo numeri e +
      telefono = telefono.replace(/[^0-9+]/g, '');
      
      // Salta righe vuote o senza dati utili
      if (!firstName && !lastName && !email && !telefono) continue;
      
      contacts.push({
        id: `csv_${i}`,
        nome: firstName || '',
        cognome: lastName || '',
        email: email || '',
        telefono: telefono || ''
      });
    }
    
    if (contacts.length === 0) {
      toast.error("Nessun contatto trovato nel file CSV");
      return;
    }
    
    setCsvContacts(contacts);
    setSelectedCsvContacts(contacts.map(c => c.id));
    toast.success(`${contacts.length} contatti trovati`);
  };

  const importCsvContacts = async () => {
    if (selectedCsvContacts.length === 0) {
      toast.error("Seleziona almeno un contatto");
      return;
    }
    
    setCsvLoading(true);
    
    try {
      const contactsToImport = csvContacts.filter(c => 
        selectedCsvContacts.includes(c.id)
      );
      
      const response = await axios.post(`${API}/clienti/import-csv`, contactsToImport, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setCsvImportOpen(false);
      setCsvContacts([]);
      setSelectedCsvContacts([]);
      // Delay per evitare conflitti DOM
      setTimeout(() => {
        fetchClienti();
      }, 150);
    } catch (error) {
      toast.error("Errore nell'importazione");
    } finally {
      setCsvLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validazione campi OBBLIGATORI (anche in modifica)
    if (!formData.nome?.trim() || !formData.cognome?.trim()) {
      toast.error("Nome e Cognome sono obbligatori");
      return;
    }
    if (!formData.telefono?.trim()) {
      toast.error("Il numero di telefono è obbligatorio");
      return;
    }
    if (!formData.genere || !["M", "F"].includes(formData.genere.toUpperCase())) {
      toast.error("Seleziona il sesso del cliente (M / F)");
      return;
    }
    try {
      if (editingCliente) {
        await axios.put(`${API}/clienti/${editingCliente.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Cliente aggiornato");
      } else {
        const response = await axios.post(`${API}/clienti`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Cliente aggiunto");
        // Imposta il filtro lettera per mostrare il nuovo cliente
        const iniziale = formData.nome.charAt(0).toUpperCase();
        setSelectedLetter(iniziale);
        setSearchTerm('');
      }
      setDialogOpen(false);
      resetForm();
      // Delay per evitare conflitti DOM
      setTimeout(() => {
        fetchClienti();
      }, 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/clienti/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Rimuovi immediatamente dalla lista per feedback istantaneo
      setClienti(prev => prev.filter(c => c.id !== id));
      toast.success("Cliente eliminato");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDialog = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nome: cliente.nome,
      cognome: cliente.cognome,
      telefono: cliente.telefono,
      email: cliente.email || "",
      data_nascita: cliente.data_nascita || "",
      genere: cliente.genere || "",
      note: cliente.note || "",
      profilo_capelli: cliente.profilo_capelli || {}
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCliente(null);
    setFormData({
      nome: "",
      cognome: "",
      telefono: "",
      email: "",
      data_nascita: "",
      genere: "",
      note: "",
      profilo_capelli: {}
    });
  };

  // ==================== CARD VIRTUALE FUNCTIONS ====================
  
  const openCardDialog = async (cliente) => {
    setSelectedClienteCard(cliente);
    setCardDialogOpen(true);
    setCardLoading(true);
    setRicaricaAmount("");
    setRicaricaNote("");
    
    try {
      const res = await axios.get(`${API}/clienti/${cliente.id}/card`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCardData(res.data);
    } catch (error) {
      setCardData(null);
    } finally {
      setCardLoading(false);
    }
  };

  const createCard = async () => {
    try {
      await axios.post(`${API}/clienti/${selectedClienteCard.id}/card`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Card creata con successo!");
      openCardDialog(selectedClienteCard);
      fetchCardsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione della card");
    }
  };

  const handleRicarica = async () => {
    const importo = parseFloat(ricaricaAmount);
    if (isNaN(importo) || importo <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    
    try {
      const res = await axios.post(
        `${API}/clienti/${selectedClienteCard.id}/card/ricarica`,
        { importo, descrizione: ricaricaNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ricarica di €${importo.toFixed(2)} effettuata!`);
      setRicaricaAmount("");
      setRicaricaNote("");
      // Ricarica i dati della card e aggiorna lo stato globale
      openCardDialog(selectedClienteCard);
      fetchCardsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella ricarica");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: it });
    } catch {
      return dateStr;
    }
  };

  // ==================== END CARD VIRTUALE ====================

  // Lettere alfabeto per filtro rapido
  const LETTERE = ['tutti', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

  // Clienti aggiunti oggi - ottimizzato
  const oggi = new Date().toISOString().split('T')[0];
  const clientiOggi = useMemo(() => {
    return clienti.filter(c => c.created_at?.startsWith(oggi));
  }, [clienti, oggi]);

  // Filtro ottimizzato con useMemo e debounce
  // Supporta ricerca multi-parola: tutte le parole devono matchare (AND logic)
  const filteredClienti = useMemo(() => {
    let risultato = clienti;
    
    // Filtro per lettera (veloce)
    if (selectedLetter !== 'tutti') {
      risultato = risultato.filter(c => 
        (c.nome || '').charAt(0).toUpperCase() === selectedLetter
      );
    }
    
    // Filtro per ricerca - supporta multi-parola (AND logic)
    if (debouncedSearch && debouncedSearch.length >= 1) {
      const searchClean = debouncedSearch.trim().toLowerCase();
      const words = searchClean.split(/\s+/).filter(w => w.length > 0);
      
      risultato = risultato.filter(c => {
        const nome = (c.nome || '').toLowerCase();
        const cognome = (c.cognome || '').toLowerCase();
        const telefono = (c.telefono || '').replace(/\s/g, '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const nomeCompleto = `${nome} ${cognome}`;
        
        // Ogni parola deve matchare in almeno uno dei campi (nome, cognome, telefono, email)
        return words.every(word => {
          const wordNoSpaces = word.replace(/\s/g, '');
          return nome.includes(word) || 
                 cognome.includes(word) ||
                 nomeCompleto.includes(word) ||
                 telefono.includes(wordNoSpaces) ||
                 email.includes(word);
        });
      });
    }
    
    // Ordina A-Z
    return risultato.sort((a, b) => {
      const nomeA = (a.nome || '').toLowerCase();
      const nomeB = (b.nome || '').toLowerCase();
      return nomeA.localeCompare(nomeB, 'it');
    });
  }, [clienti, selectedLetter, debouncedSearch]);

  // Paginazione
  const totalePagine = Math.ceil(filteredClienti.length / CLIENTI_PER_PAGINA);
  const clientiPaginati = useMemo(() => {
    const inizio = (paginaCorrente - 1) * CLIENTI_PER_PAGINA;
    return filteredClienti.slice(inizio, inizio + CLIENTI_PER_PAGINA);
  }, [filteredClienti, paginaCorrente]);

  // Reset pagina quando cambia filtro
  useEffect(() => {
    setPaginaCorrente(1);
  }, [selectedLetter, debouncedSearch]);

  // Conta clienti per lettera (ottimizzato)
  const conteggioLettere = useMemo(() => {
    const conteggio = {};
    clienti.forEach(c => {
      const lettera = (c.nome || '').charAt(0).toUpperCase();
      if (lettera) {
        conteggio[lettera] = (conteggio[lettera] || 0) + 1;
      }
    });
    return conteggio;
  }, [clienti]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="clienti">
      {/* Banner ritorno all'Agenda */}
      {returnToAgenda && (
        <div className="flex items-center justify-between gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="back-to-agenda-banner">
          <div className="flex items-center gap-2 text-blue-700 text-sm">
            <CalendarDays className="w-4 h-4 shrink-0" />
            <span>Arrivato dall'Agenda</span>
          </div>
          <Button
            onClick={handleBackToAgenda}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="back-to-agenda-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Torna all'Agenda
          </Button>
        </div>
      )}

      {/* Header con ricerca */}
      <div className="flex flex-col gap-2 sm:gap-4">
        {/* Riga 1: Ricerca */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cerca cliente..."
            className="pl-9 h-9 sm:h-10 text-sm"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value) setSelectedLetter('tutti');
            }}
            data-testid="search-clienti"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Riga 2: Pulsanti */}
        <div className="flex gap-2 justify-end">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setCsvImportOpen(true)}
            className="h-8 px-2 sm:px-3 text-xs sm:text-sm border-green-200 text-green-600 hover:bg-green-50"
          >
            <FileSpreadsheet className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Importa CSV</span>
          </Button>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-8 px-2 sm:px-3 text-xs sm:text-sm bg-blue-600 hover:bg-blue-700" data-testid="add-cliente-btn">
                <Plus className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Nuovo</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editingCliente ? "Modifica Cliente" : "Nuovo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Bottone Conferma in alto — sempre visibile */}
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 sticky top-0 z-10 shadow-md"
                data-testid="submit-cliente"
              >
                {editingCliente ? "Salva Modifiche" : "Aggiungi Cliente"}
              </Button>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    data-testid="input-nome"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input
                    value={formData.cognome}
                    onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                    required
                    data-testid="input-cognome"
                  />
                </div>
              </div>

              {/* Warning cliente esistente */}
              {clienteEsistente && !editingCliente && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {(clienteEsistente.nome || '').charAt(0)}{(clienteEsistente.cognome || '').charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="text-amber-800 font-medium text-sm">
                      ⚠️ Cliente già esistente!
                    </p>
                    <p className="text-amber-700 text-sm">
                      {clienteEsistente.nome} {clienteEsistente.cognome} 
                      {clienteEsistente.telefono && ` - ${clienteEsistente.telefono}`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDialogOpen(false);
                      setSearchTerm(`${clienteEsistente.nome} ${clienteEsistente.cognome}`);
                    }}
                    className="text-amber-600 text-xs underline hover:text-amber-800"
                  >
                    Vai al cliente
                  </button>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefono *</Label>
                  <Input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    required
                    data-testid="input-telefono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Cake className="w-4 h-4 text-pink-500" />
                    Data di Nascita
                  </Label>
                  <Input
                    type="date"
                    value={formData.data_nascita}
                    onChange={(e) => setFormData({ ...formData, data_nascita: e.target.value })}
                    data-testid="input-data-nascita"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {formData.data_nascita && (
                    <p className="text-xs text-slate-500">
                      Età: {calcolaEta(formData.data_nascita)} anni
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-500" />
                    Sesso <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <Select 
                    value={formData.genere} 
                    onValueChange={(value) => setFormData({ ...formData, genere: value })}
                  >
                    <SelectTrigger data-testid="select-genere" className={!formData.genere ? "border-red-300" : ""}>
                      <SelectValue placeholder="Seleziona M / F (obbligatorio)..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="F">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                          Donna
                        </span>
                      </SelectItem>
                      <SelectItem value="M">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          Uomo
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Profilo Capelli - Sezione espandibile */}
              <div className="space-y-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <Label className="font-semibold text-amber-800">Profilo Capelli (Consulenza)</Label>
                </div>
                <ProfiloCapelliForm 
                  profilo={formData.profilo_capelli || {}}
                  onChange={(p) => setFormData({ ...formData, profilo_capelli: p })}
                  compact={true}
                />
              </div>

              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Preferenze, allergie, trattamenti preferiti..."
                  rows={2}
                  data-testid="input-note"
                />
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Barra lettere A-Z */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {LETTERE.map(lettera => {
          const count = lettera === 'tutti' 
            ? clienti.length 
            : (conteggioLettere[lettera] || 0);
          return (
            <button
              key={lettera}
              onClick={() => {
                setSelectedLetter(lettera);
                setSearchTerm('');
              }}
              className={`px-2 py-1 rounded text-sm font-medium transition-all duration-150 min-w-[32px] ${
                selectedLetter === lettera
                  ? 'bg-blue-600 text-white scale-105'
                  : count > 0
                  ? 'bg-slate-100 text-slate-700 hover:bg-blue-100 hover:text-blue-700'
                  : 'bg-slate-50 text-slate-300 cursor-not-allowed'
              }`}
              disabled={count === 0 && lettera !== 'tutti'}
            >
              {lettera === 'tutti' ? 'Tutti' : lettera}
            </button>
          );
        })}
      </div>

      {/* Clienti aggiunti OGGI */}
      {clientiOggi.length > 0 && (
        <div className="mb-4 p-4 bg-green-50 rounded-xl border border-green-200">
          <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Aggiunti oggi ({clientiOggi.length})
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {clientiOggi.map(cliente => (
              <div
                key={cliente.id}
                className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg shadow-sm min-w-fit"
              >
                <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">
                  {(cliente.nome || '').charAt(0)}{(cliente.cognome || '').charAt(0)}
                </div>
                <div>
                  <p className="text-base font-semibold text-slate-800 whitespace-nowrap">
                    {cliente.nome} {cliente.cognome}
                  </p>
                  <p className="text-sm text-slate-500">{cliente.telefono}</p>
                </div>
                <button
                  onClick={() => handleDelete(cliente.id)}
                  className="ml-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span className="flex items-center gap-1">
          <Users className="w-4 h-4" />
          {filteredClienti.length} clienti
          {selectedLetter !== 'tutti' && ` con ${selectedLetter}`}
          {searchTerm && ` per "${searchTerm}"`}
        </span>
        <span>Mostrando {Math.min(paginaCorrente * CLIENTI_PER_PAGINA, filteredClienti.length)} di {filteredClienti.length}</span>
      </div>

      {/* Banner Alert Anagrafica Incompleta */}
      {clientiAnagraficaIncompleta.length > 0 && (
        <div
          className="mb-3 flex items-center gap-3 p-3 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 hover:border-amber-500 transition-all"
          data-testid="banner-anagrafica-incompleta"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shrink-0 shadow animate-pulse">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm sm:text-base font-bold text-amber-900">
              {clientiAnagraficaIncompleta.length} {clientiAnagraficaIncompleta.length === 1 ? 'cliente ha' : 'clienti hanno'} anagrafica incompleta
            </p>
            <p className="text-xs text-amber-700">
              Clicca l&apos;icona ⚙️ (lampeggiante) sulla scheda cliente per completare i campi mancanti (telefono, data nascita).
            </p>
          </div>
        </div>
      )}



      {/* Clients Grid - paginato */}
      {clientiPaginati.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {clientiPaginati.map((cliente) => (
              <div 
                key={cliente.id} 
                className="group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-4 border border-slate-100"
                data-testid={`cliente-card-${cliente.id}`}
              >
                  <div>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-12 h-12 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-sm flex-shrink-0 ${
                        cliente.genere === 'F' 
                          ? 'bg-pink-500' 
                          : cliente.genere === 'M'
                          ? 'bg-blue-500'
                          : 'bg-slate-500'
                      }`}>
                        {(cliente.nome || '').charAt(0)}{(cliente.cognome || '').charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-base sm:text-sm text-slate-900 leading-tight break-words line-clamp-2 flex items-start gap-1.5" title={`${cliente.nome} ${cliente.cognome}`}>
                          <span className="flex-1">{cliente.nome} {cliente.cognome}</span>
                          {cliente.is_starred && (
                            <Sparkles className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0 mt-0.5" />
                          )}
                        </h3>
                        <p className="text-sm sm:text-xs text-slate-600 flex items-center gap-1 mt-0.5 min-w-0">
                          <Phone className="w-4 h-4 sm:w-3 sm:h-3 flex-shrink-0" />
                          <span className="truncate">{cliente.telefono || 'N/D'}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Badge GDPR */}
                    {cliente.gdpr_firmato && (
                      <div className="absolute top-2 right-2 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        GDPR
                      </div>
                    )}
                    
                    {/* Badge Consulenza Fatta */}
                    {consulenzeStatus[cliente.id] && (
                      <div className={`absolute top-2 ${cliente.gdpr_firmato ? 'right-16' : 'right-2'} bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1`}>
                        <ClipboardCheck className="w-3 h-3" />
                        Consulenza
                      </div>
                    )}
                    
                    {/* Pallino Rosso - Consulenza Mancante */}
                    {!consulenzeStatus[cliente.id] && (
                      <div 
                        className={`absolute top-2 ${cliente.gdpr_firmato ? 'right-16' : 'right-2'} flex items-center gap-1`}
                        title="Consulenza non effettuata"
                      >
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                        </span>
                      </div>
                    )}
                    
                    {/* Pulsanti azione - sempre visibili su touch (iPad/mobile), hover su desktop */}
                    <div className="flex gap-1 justify-end mt-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-150">
                      {/* Pulsante Storico */}
                      <button
                        className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                        onClick={() => {
                          setSelectedClienteStorico(cliente);
                          setStoricoOpen(true);
                        }}
                        title="Storico Trattamenti"
                        data-testid={`storico-btn-${cliente.id}`}
                      >
                        <History className="w-4 h-4 text-purple-500" />
                      </button>
                      {/* Pulsante Card/Wallet */}
                      <button
                        className={`p-2 rounded-lg transition-colors ${
                          cardsStatus[cliente.id]?.saldo > 0 
                            ? 'hover:bg-emerald-100 bg-emerald-50' 
                            : 'hover:bg-slate-100'
                        }`}
                        onClick={() => openCardDialog(cliente)}
                        title={cardsStatus[cliente.id]?.saldo > 0 
                          ? `Card: €${cardsStatus[cliente.id].saldo.toFixed(2)}` 
                          : "Gestisci Card"
                        }
                        data-testid={`card-btn-${cliente.id}`}
                      >
                        <Wallet className={`w-4 h-4 ${
                          cardsStatus[cliente.id]?.saldo > 0 
                            ? 'text-emerald-600' 
                            : 'text-slate-400'
                        }`} />
                      </button>
                      <button
                        className={`p-2 rounded-lg transition-colors ${cliente.gdpr_firmato ? 'hover:bg-green-100' : 'hover:bg-amber-100'}`}
                        onClick={() => {
                          setSelectedClienteGDPR(cliente);
                          setGdprDialogOpen(true);
                        }}
                        title={cliente.gdpr_firmato ? "GDPR firmato" : "Firma GDPR"}
                        data-testid={`gdpr-btn-${cliente.id}`}
                      >
                        <FileSignature className={`w-4 h-4 ${cliente.gdpr_firmato ? 'text-green-500' : 'text-amber-500'}`} />
                      </button>
                      <button
                        className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                        onClick={() => openEditDialog(cliente)}
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </button>
                      {/* Ingranaggio Anagrafica con lampeggio se mancano campi */}
                      {(() => {
                        const mancanti = getCampiMancanti(cliente);
                        const haCampiMancanti = mancanti.length > 0;
                        return (
                          <button
                            className={`relative p-2 rounded-lg transition-colors ${haCampiMancanti ? 'bg-amber-100 hover:bg-amber-200 animate-pulse' : 'hover:bg-slate-100'}`}
                            onClick={() => openEditDialog(cliente)}
                            title={haCampiMancanti ? `Mancano: ${mancanti.join(', ')}` : 'Anagrafica completa'}
                            data-testid={`anagrafica-${cliente.id}`}
                          >
                            <Settings className={`w-4 h-4 ${haCampiMancanti ? 'text-amber-700' : 'text-slate-500'}`} />
                            {haCampiMancanti && (
                              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center shadow ring-2 ring-white">
                                {mancanti.length}
                              </span>
                            )}
                          </button>
                        );
                      })()}
                      <button
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                        onClick={() => handleDelete(cliente.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
              </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun cliente trovato</p>
          {searchTerm && (
            <Button variant="link" onClick={() => setSearchTerm('')} className="mt-2">
              Cancella ricerca
            </Button>
          )}
        </div>
      )}

      {/* Carica altri */}
      {paginaCorrente < totalePagine && (
        <div className="flex justify-center mt-4">
          <Button
            variant="outline"
            onClick={() => setPaginaCorrente(prev => prev + 1)}
            className="gap-2"
          >
            <ChevronDown className="w-4 h-4" />
            Carica altri {Math.min(CLIENTI_PER_PAGINA, filteredClienti.length - paginaCorrente * CLIENTI_PER_PAGINA)} clienti
          </Button>
        </div>
      )}

      {/* Card Virtuale Dialog */}
      {/* Dialog Gestione Card Cliente */}
      <Dialog open={cardDialogOpen} onOpenChange={setCardDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <CreditCard className="w-5 h-5 text-blue-600" />
              Gestione Card - {selectedClienteCard?.nome} {selectedClienteCard?.cognome}
            </DialogTitle>
          </DialogHeader>
          
          {selectedClienteCard && (
            <CardCliente 
              cliente={selectedClienteCard} 
              onUpdate={() => {
                // Refresh se necessario
              }} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Importa da Google Contacts */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) setNeedsGoogleConnect(false);
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Importa Contatti da Google
            </DialogTitle>
          </DialogHeader>
          
          {needsGoogleConnect ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-6">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-10 h-10" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div className="text-center space-y-2">
                <h3 className="font-semibold text-lg text-slate-800">Connetti Google Contacts</h3>
                <p className="text-slate-500 max-w-sm">
                  Per importare i tuoi contatti, devi prima autorizzare l'accesso a Google Contacts.
                </p>
              </div>
              <Button 
                onClick={connectGoogle}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Connetti con Google
              </Button>
              
              <div className="text-center pt-4 border-t w-full">
                <p className="text-xs text-slate-400 mb-2">
                  Se ricevi un errore durante la connessione, vai su Impostazioni → Google Calendar per configurare le credenziali OAuth.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setImportDialogOpen(false)}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          ) : fetchingContacts ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
              <p className="text-slate-500">Recupero contatti da Google...</p>
            </div>
          ) : googleContacts.length > 0 ? (
            <div className="space-y-4">
              {/* Header con contatori e azioni */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-blue-600">{selectedContacts.length}</span> di {googleContacts.length} contatti selezionati
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAllContacts}>
                    Seleziona tutti
                  </Button>
                  <Button variant="ghost" size="sm" onClick={deselectAllContacts}>
                    Deseleziona
                  </Button>
                </div>
              </div>

              {/* Lista contatti */}
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {googleContacts.map(contact => {
                  const isSelected = selectedContacts.includes(contact.google_id);
                  const hasData = contact.telefono || contact.email;
                  
                  return (
                    <div
                      key={contact.google_id}
                      onClick={() => toggleContactSelection(contact.google_id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-300' 
                          : hasData 
                            ? 'bg-white border-slate-200 hover:border-blue-200' 
                            : 'bg-slate-50 border-slate-200 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                          {contact.nome?.charAt(0) || contact.cognome?.charAt(0) || '?'}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800">
                            {contact.nome} {contact.cognome}
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm text-slate-500">
                            {contact.telefono && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contact.telefono}
                              </span>
                            )}
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {contact.email}
                              </span>
                            )}
                            {!contact.telefono && !contact.email && (
                              <span className="text-amber-500 text-xs">Nessun contatto</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer con pulsante importa */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={importSelectedContacts}
                  disabled={selectedContacts.length === 0 || importLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importazione...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Importa {selectedContacts.length} Contatti
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Nessun contatto trovato</h3>
              <p className="text-sm text-slate-500">
                Non sono stati trovati contatti nel tuo account Google.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Importa da CSV */}
      <Dialog open={csvImportOpen} onOpenChange={(open) => {
        setCsvImportOpen(open);
        if (!open) {
          setCsvContacts([]);
          setSelectedCsvContacts([]);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
              Importa Contatti da CSV
            </DialogTitle>
          </DialogHeader>
          
          {csvContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <Upload className="w-10 h-10 text-green-600" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="font-semibold text-lg text-slate-800">Carica file CSV</h3>
                <p className="text-slate-500 max-w-sm">
                  Esporta i tuoi contatti da Google Contacts come CSV e caricali qui.
                </p>
                <p className="text-xs text-slate-400">
                  Google Contacts → Menu (⋮) → Esporta → Google CSV
                </p>
              </div>
              
              <label className="cursor-pointer">
                <input 
                  type="file" 
                  accept=".csv"
                  onChange={handleCsvFileUpload}
                  className="hidden"
                />
                <div className="px-8 py-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Seleziona File CSV
                </div>
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {csvContacts.length} contatti trovati • {selectedCsvContacts.length} selezionati
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedCsvContacts(csvContacts.map(c => c.id))}
                  >
                    Seleziona tutti
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedCsvContacts([])}
                  >
                    Deseleziona
                  </Button>
                </div>
              </div>
              
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {csvContacts.map((contact) => {
                  const isSelected = selectedCsvContacts.includes(contact.id);
                  return (
                    <div
                      key={contact.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedCsvContacts(prev => prev.filter(id => id !== contact.id));
                        } else {
                          setSelectedCsvContacts(prev => [...prev, contact.id]);
                        }
                      }}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-green-600 border-green-600' : 'border-slate-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800">
                            {contact.nome} {contact.cognome}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            {contact.telefono && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contact.telefono}
                              </span>
                            )}
                            {contact.email && (
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3" />
                                {contact.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setCsvContacts([]);
                    setSelectedCsvContacts([]);
                  }}
                >
                  Cambia File
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCsvImportOpen(false)}>
                    Annulla
                  </Button>
                  <Button 
                    onClick={importCsvContacts}
                    disabled={selectedCsvContacts.length === 0 || csvLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {csvLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importazione...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Importa {selectedCsvContacts.length} Contatti
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog GDPR */}
      <GDPRConsent
        cliente={selectedClienteGDPR}
        open={gdprDialogOpen}
        onOpenChange={setGdprDialogOpen}
        onSuccess={() => {
          fetchClienti();
          setSelectedClienteGDPR(null);
        }}
      />

      {/* Dialog Storico Cliente */}
      <StoricoCliente
        clienteId={selectedClienteStorico?.id}
        clienteNome={selectedClienteStorico ? `${selectedClienteStorico.nome} ${selectedClienteStorico.cognome}` : ''}
        open={storicoOpen}
        onOpenChange={(open) => {
          setStoricoOpen(open);
          // Se chiude e c'è returnToAgenda, mostra opzione per tornare
          if (!open && returnToAgenda) {
            // Torna automaticamente all'agenda dopo aver chiuso
            setTimeout(() => {
              handleBackToAgenda();
            }, 100);
          }
        }}
        token={token}
        showBackToAgenda={returnToAgenda !== null}
        onBackToAgenda={handleBackToAgenda}
      />
    </div>
  );
}
